import '@wangeditor/editor/dist/css/style.css';

import type { IDomEditor, IEditorConfig } from '@wangeditor/editor';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { message } from 'antd';
import { type CSSProperties, useCallback, useMemo, useState } from 'react';
import { apiUploadOss } from '@/pages/site/site-data/api';

export interface WangRichTextProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  /** 编辑区高度（不含工具栏），默认 420 */
  height?: number;
  readOnly?: boolean;
  className?: string;
  style?: CSSProperties;
  /**
   * 自定义上传，返回可访问的 URL（默认走站点 OSS：`/api/oss/upload`）
   * 图片、视频插入均复用此逻辑，便于统一鉴权与域名
   */
  uploadFile?: (file: File) => Promise<string>;
}

async function defaultUploadFile(file: File): Promise<string> {
  const res = await apiUploadOss(file);
  if (res.code !== 200 || !res.data) {
    throw new Error(res.message ?? '上传失败');
  }
  return res.data as string;
}

/** 判断是否仅有空段落 / 空白，用于富文本必填校验 */
export function isRichTextEmpty(html: string | undefined | null): boolean {
  if (html == null || html === '') return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|\u200b/gi, '')
    .trim();
  return text.length === 0;
}

export default function WangRichText({
  value = '',
  onChange,
  placeholder = '请输入正文',
  height = 420,
  readOnly = false,
  className,
  style,
  uploadFile = defaultUploadFile,
}: WangRichTextProps) {
  const [editor, setEditor] = useState<IDomEditor | null>(null);

  const editorConfig = useMemo<Partial<IEditorConfig>>(() => {
    const upload = async (file: File) => {
      return uploadFile(file);
    };

    return {
      placeholder,
      readOnly,
      MENU_CONF: {
        uploadImage: {
          async customUpload(
            file: File,
            insertFn: (url: string, alt: string, href: string) => void,
          ) {
            try {
              const url = await upload(file);
              insertFn(url, file.name, url);
            } catch (e) {
              message.error((e as Error).message || '图片上传失败');
            }
          },
        },
        uploadVideo: {
          async customUpload(file: File, insertFn: (url: string) => void) {
            try {
              const url = await upload(file);
              insertFn(url);
            } catch (e) {
              message.error((e as Error).message || '视频上传失败');
            }
          },
        },
      },
    };
  }, [placeholder, readOnly, uploadFile]);

  const handleChange = useCallback(
    (ed: IDomEditor) => {
      onChange?.(ed.getHtml());
    },
    [onChange],
  );

  const toolbarConfig = useMemo(() => ({}), []);

  return (
    <div
      className={className}
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: 6,
        overflow: 'hidden',
        ...style,
      }}
    >
      <Toolbar
        editor={editor}
        defaultConfig={toolbarConfig}
        mode="default"
        style={{ borderBottom: '1px solid #d9d9d9' }}
      />
      <Editor
        defaultConfig={editorConfig}
        value={value}
        onCreated={setEditor}
        onChange={handleChange}
        mode="default"
        style={{ height, overflowY: 'hidden' }}
      />
    </div>
  );
}
